import React from 'react';
import { ActionButton, type ActionButtonData } from '../action-button/ActionButton';
import { ProfilePage, type ProfilePageData } from './ProfilePage';
import { isOrchestratedProfileData, useProfile, type OrchestratedProfileDialogData } from './useProfile';

export type ProfileDialogData = ProfilePageData & {
  /** When false, render nothing. Defaults to true so harnesses work without a host flag. */
  profileDialogEnabled?: boolean;
  /**
   * When true, skip the outer Bootstrap modal shell (overlay + `#profile-modal`)
   * because Angular already owns that chrome. Close controls still render.
   */
  embedInHostModal?: boolean;
  closeActionData?: ActionButtonData;
  onClose?: () => void;
};

type Props = Readonly<{
  data?: ProfileDialogData | OrchestratedProfileDialogData;
}>;

function isActivationKey(key: string): boolean {
  return key === 'Enter' || key === ' ';
}

function runCloseOnActivationKey(event: React.KeyboardEvent, onClose?: () => void) {
  if (!isActivationKey(event.key)) {
    return;
  }
  event.preventDefault();
  onClose?.();
}

function isDialogEnabled(data: ProfileDialogData | undefined): data is ProfileDialogData {
  return Boolean(data) && data.profileDialogEnabled !== false;
}

function MobileClose({ onClose, dismissModal }: Readonly<{ onClose?: () => void; dismissModal?: boolean }>) {
  return (
    <div
      id="close-profile-button-mobile"
      className="icon-x modal-close-button"
      role="button"
      tabIndex={0}
      aria-label="Close"
      data-dismiss={dismissModal ? 'modal' : undefined}
      onClick={() => onClose?.()}
      onKeyDown={(event) => runCloseOnActivationKey(event, onClose)}
    />
  );
}

function desktopCloseSlot(data: ProfileDialogData) {
  if (!data.closeActionData) {
    return null;
  }

  return (
    <span className="profile-close-action-button">
      <ActionButton
        data={{
          ...data.closeActionData,
          onClick: data.onClose ?? data.closeActionData.onClick,
        }}
      />
    </span>
  );
}

function shouldDismissHostModal(data: ProfileDialogData): boolean {
  return Boolean(data.embedInHostModal) || Boolean(data.closeActionData?.dismissModal);
}

function toPageData(data: ProfileDialogData): ProfilePageData {
  return {
    ...data,
    profilePageEnabled: data.profilePageEnabled !== false,
    mobileCloseSlot: (
      <MobileClose onClose={data.onClose} dismissModal={shouldDismissHostModal(data)} />
    ),
    desktopCloseSlot: desktopCloseSlot(data),
  };
}

function EmbeddedProfileDialog({ page }: Readonly<{ page: React.ReactNode }>) {
  return (
    <div className="modal-dialog-profile" role="document" data-testid="profile-dialog-embedded">
      {page}
    </div>
  );
}

function ProfileModalOverlay({ onClose }: Readonly<{ onClose?: () => void }>) {
  return (
    <div
      className="modal-overlay"
      aria-label="Close"
      role="button"
      tabIndex={0}
      onClick={() => onClose?.()}
      onKeyDown={(event) => runCloseOnActivationKey(event, onClose)}
    />
  );
}

function StandaloneProfileDialog({
  onClose,
  page,
}: Readonly<{ onClose?: () => void; page: React.ReactNode }>) {
  return (
    <div
      className="modal modal-profile fade in"
      id="profile-modal"
      tabIndex={-1}
      role="dialog"
      aria-labelledby="profile-modal-label"
      data-testid="profile-dialog"
      style={{ display: 'block' }}
    >
      <object
        type="image/svg+xml"
        data="assets/icons/profile-icons.svg"
        style={{ display: 'none' }}
        aria-hidden="true"
      />
      <ProfileModalOverlay onClose={onClose} />
      <div className="modal-dialog-profile" role="document">
        {page}
      </div>
    </div>
  );
}

function PresentedProfileDialog({ data }: Readonly<{ data?: ProfileDialogData }>) {
  if (!isDialogEnabled(data)) {
    return null;
  }

  const page = <ProfilePage data={toPageData(data)} />;

  if (data.embedInHostModal) {
    return <EmbeddedProfileDialog page={page} />;
  }

  return <StandaloneProfileDialog onClose={data.onClose} page={page} />;
}

function OrchestratedProfileDialog({ data }: Readonly<{ data: OrchestratedProfileDialogData }>) {
  const projected = useProfile(data);
  return <PresentedProfileDialog data={projected} />;
}

/**
 * Modal chrome around ProfilePage (RNA-5276). When `data.orchestrated` is set,
 * `useProfile` owns loads and actions (RNA-5279). Close controls live only here.
 */
export function ProfileDialog({ data }: Props) {
  if (isOrchestratedProfileData(data)) {
    if (data.profileDialogEnabled === false) {
      return null;
    }
    return <OrchestratedProfileDialog data={data} />;
  }

  return <PresentedProfileDialog data={data} />;
}
